'use client';

import { useTranslations } from 'next-intl';
import { Check } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { NoticeBanner } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { createLogger } from '@/shared/lib/logger';
import type {
  AssistantActionCardBlock,
  AssistantBlock,
  AssistantCardOutcome,
  AssistantCommandBlock,
  AssistantNoticeBlock,
  AssistantOptionsBlock,
  AssistantTextBlock,
} from '@/types/assistant';
import { undoKey, useAssistantStore } from '../model/assistant.store';
import { useRenderStore } from '@/features/design/render/model/render-form.store';
import { undoPatch } from '../lib/settings-patch';
import { AssistantMarkdown } from './AssistantMarkdown';

const logger = createLogger('Assistant');
const loggedUnknown = new Set<string>();

export interface AssistantBlocksProps {
  messageId: string;
  blocks: Array<AssistantBlock | { type: string }>;
  /** Options answer only the latest assistant message, and not mid-turn. */
  optionsEnabled: boolean;
  onOption: (label: string) => void;
  onAction: (card: AssistantActionCardBlock) => void;
  /**
   * A card button was pressed; the panel stores the outcome on the server.
   * Without it (moment bubble, voice refusal) the outcome stays on this device.
   */
  onCardOutcome?: (messageId: string, blockIndex: number, outcome: AssistantCardOutcome) => void;
}

/** One assistant message: its typed blocks, in the order the API sent them. */
export function AssistantBlocks({
  messageId,
  blocks,
  optionsEnabled,
  onOption,
  onAction,
  onCardOutcome,
}: AssistantBlocksProps) {
  return (
    <div className="space-y-3 text-composer-input text-foreground">
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'text':
            return <AssistantMarkdown key={index} text={(block as AssistantTextBlock).text} />;
          case 'options':
            return (
              <OptionChips
                key={index}
                block={block as AssistantOptionsBlock}
                enabled={optionsEnabled}
                onOption={onOption}
              />
            );
          case 'action_card':
            return (
              <ActionCard
                key={index}
                cardKey={undoKey(messageId, index)}
                card={block as AssistantActionCardBlock}
                onAction={onAction}
                onOutcome={(outcome) =>
                  onCardOutcome
                    ? onCardOutcome(messageId, index, outcome)
                    : useAssistantStore.getState().setCardOutcome(undoKey(messageId, index), outcome)
                }
              />
            );
          case 'client_command':
            return (block as AssistantCommandBlock).command === 'apply_render_settings' ? (
              <SettingsApplied key={index} undoKeyValue={undoKey(messageId, index)} />
            ) : null;
          case 'notice': {
            const notice = block as AssistantNoticeBlock;
            return (
              <NoticeBanner
                key={index}
                variant={notice.level in VARIANTS ? notice.level : 'info'}
                message={notice.text}
              />
            );
          }
          default:
            if (!loggedUnknown.has(block.type)) {
              loggedUnknown.add(block.type);
              logger.info('Ignoring unknown assistant block', { type: block.type });
            }
            return null;
        }
      })}
    </div>
  );
}

const VARIANTS = { info: 1, warning: 1, error: 1, success: 1 } as const;

function OptionChips({
  block,
  enabled,
  onOption,
}: {
  block: AssistantOptionsBlock;
  enabled: boolean;
  onOption: (label: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {block.options.map((option) => (
        <button
          key={option.label}
          type="button"
          disabled={!enabled}
          onClick={() => onOption(option.label)}
          className={cn(
            'min-h-9 rounded-full bg-settings-card px-4 py-2 text-chip text-foreground text-start transition-colors',
            enabled
              ? 'hover:bg-muted active:scale-[0.98]'
              : 'opacity-60 cursor-default'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ActionCard({
  cardKey,
  card,
  onAction,
  onOutcome,
}: {
  cardKey: string;
  card: AssistantActionCardBlock;
  onAction: (card: AssistantActionCardBlock) => void;
  onOutcome: (outcome: AssistantCardOutcome) => void;
}) {
  // The server's record wins (another device, a reload); the local one
  // covers the moment between the press and the re-read thread.
  const local = useAssistantStore((s) => s.cardOutcomes[cardKey]);
  const outcome = card.outcome ?? local;
  if (outcome === 'dismissed') return null;
  // Pressed: the action now runs on its own screen (its progress, its errors
  // and its paywall live there), so the card keeps only what it was, with a
  // check, and cannot be pressed a second time.
  const started = outcome === 'started';
  // A card of an earlier conversation keeps its text, without buttons.
  const pressable = card.is_actionable && !started;

  return (
    <div className="rounded-[12px] bg-settings-card p-3 space-y-2">
      <div className="flex items-start gap-2 text-form-label text-foreground">
        {started && <Check className="mt-0.5 size-4 flex-shrink-0 text-primary" />}
        <span>{card.title}</span>
      </div>
      {card.cost?.label && (
        <div className="text-card-meta text-muted-foreground">{card.cost.label}</div>
      )}
      {pressable && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            size="sm"
            className="h-9 px-4"
            onClick={() => {
              onOutcome('started');
              onAction(card);
            }}
          >
            {card.primary}
          </Button>
          <Button size="sm" variant="outline" className="h-9 px-4" onClick={() => onOutcome('dismissed')}>
            {card.secondary}
          </Button>
        </div>
      )}
    </div>
  );
}

function SettingsApplied({ undoKeyValue }: { undoKeyValue: string }) {
  const t = useTranslations('assistant');
  const undo = useAssistantStore((s) => s.undo[undoKeyValue]);
  const loadedDesignId = useRenderStore((s) => s.currentContext?.conceptId);
  // Undo restores this device's form, so it needs that design's form loaded;
  // a message from an earlier session has no local snapshot to restore.
  const canUndo = !!undo && !undo.undone && loadedDesignId === undo.designId;

  return (
    <div className="flex items-center gap-2 text-card-meta text-muted-foreground">
      <Check className="size-4 text-primary flex-shrink-0" strokeWidth={2} />
      <span>{undo?.undone ? t('settingsRestored') : t('settingsApplied')}</span>
      {canUndo && (
        <button
          type="button"
          onClick={() => undoPatch(undoKeyValue)}
          className="ms-auto font-medium text-primary hover:underline"
        >
          {t('undo')}
        </button>
      )}
    </div>
  );
}
